import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";
import type { paths } from "../../api/generated/api-types";

type TaskDetailResponse =
  paths["/tasks/{taskId}"]["get"]["responses"]["200"]["content"]["application/json"];

export type TaskInfo = TaskDetailResponse["task"];
export type AnchorInfo = TaskDetailResponse["anchors"][number];
export type ArticleTopicInfo = TaskDetailResponse["articleTopics"][number];
export type PublicationInfo = NonNullable<TaskDetailResponse["publication"]>;
export type PublicationItemInfo = PublicationInfo["items"][number];

export interface LogInfo {
  id: string;
  entityId: string;
  entityType: string;
  publicationId: string | null;
  proxyId: string | null;
  traceId: string | null;
  message: string;
  createdAt: string;
}

export type ScreenState =
  | { kind: "loading" }
  | {
      kind: "data";
      task: TaskInfo;
      anchors: AnchorInfo[];
      articleTopics: ArticleTopicInfo[];
      publication: PublicationInfo | null;
      logs: LogInfo[];
    }
  | { kind: "error" };

export interface UseTaskDetailSubscriptionResult {
  state: ScreenState;
}

export function useTaskDetailSubscription(): UseTaskDetailSubscriptionResult {
  const { id: taskId } = useParams<{ id: string }>();
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const clientRef = useRef<SseClient | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined = undefined;
    if (taskId === undefined || taskId === "") {
      setState({ kind: "error" });
    } else {
      const client = createSseClient({
        url: `/api/tasks/${taskId}/subscribe`,
        events: {
          initial(data: unknown) {
            const payload = data as TaskDetailResponse;
            setState({
              kind: "data",
              task: payload.task,
              anchors: payload.anchors,
              articleTopics: payload.articleTopics,
              publication: payload.publication,
              logs: payload.logs,
            });
          },

          "articleTopic:updated"(data: unknown) {
            const payload = data as {
              task: { phase: TaskInfo["phase"]; phaseStatus: string };
              articleTopic: ArticleTopicInfo;
            };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  task: { ...previous.task, phase: payload.task.phase, phaseStatus: payload.task.phaseStatus },
                  articleTopics: previous.articleTopics.map((topic) =>
                    topic.id === payload.articleTopic.id ? payload.articleTopic : topic,
                  ),
                };
              }
              return next;
            });
          },

          "anchor:updated"(data: unknown) {
            const payload = data as {
              task: { phase: TaskInfo["phase"]; phaseStatus: string };
              anchor: AnchorInfo;
            };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  task: { ...previous.task, phase: payload.task.phase, phaseStatus: payload.task.phaseStatus },
                  anchors: previous.anchors.map((anchor) =>
                    anchor.id === payload.anchor.id ? payload.anchor : anchor,
                  ),
                };
              }
              return next;
            });
          },

          "publication:created"(data: unknown) {
            const payload = data as {
              task: { phase: TaskInfo["phase"]; phaseStatus: string };
              publication: PublicationInfo | null;
            };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  task: { ...previous.task, phase: payload.task.phase, phaseStatus: payload.task.phaseStatus },
                  publication: payload.publication,
                };
              }
              return next;
            });
          },

          "publication:updated"(data: unknown) {
            const payload = data as { publication: PublicationInfo | null };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = { ...previous, publication: payload.publication };
              }
              return next;
            });
          },

          "item:updated"(data: unknown) {
            const payload = data as {
              task: { phase: TaskInfo["phase"]; phaseStatus: string; lastPublishedAt: string | null };
              item: PublicationItemInfo;
            };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data" && previous.publication !== null) {
                next = {
                  ...previous,
                  task: { ...previous.task, phase: payload.task.phase, phaseStatus: payload.task.phaseStatus, lastPublishedAt: payload.task.lastPublishedAt },
                  publication: {
                    ...previous.publication,
                    items: previous.publication.items.map((item) =>
                      item.id === payload.item.id ? payload.item : item,
                    ),
                  },
                };
              }
              return next;
            });
          },

          "task:updated"(data: unknown) {
            const payload = data as {
              task: Pick<TaskInfo, "deactivatedAt" | "deactivatedByName" | "deactivationReason">;
            };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  task: {
                    ...previous.task,
                    deactivatedAt: payload.task.deactivatedAt,
                    deactivatedByName: payload.task.deactivatedByName,
                    deactivationReason: payload.task.deactivationReason,
                  },
                };
              }
              return next;
            });
          },

          log(data: unknown) {
            const payload = data as { log: LogInfo };

            setState((previous) => {
              let next: ScreenState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  logs: [...previous.logs, payload.log],
                };
              }
              return next;
            });
          },
        },
        onError() {
          setState({ kind: "error" });
        },
      });

      clientRef.current = client;

      unsubscribe = () => {
        client.close();
        clientRef.current = null;
      };
    }
    return unsubscribe;
  }, [taskId]);

  return { state };
}
